/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Video Generator Driver Factory & Resolution Resolver
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type {
  VideoGeneratorDriver,
  VideoProviderCode,
  VideoAspectRatio,
} from './types';
import { FalVideoDriver } from './drivers/fal-video.driver';
import { RunwayVideoDriver } from './drivers/runway-video.driver';
import { SynthesiaVideoDriver } from './drivers/synthesia.driver';

/**
 * Standard video pixel dimensions mapped from aspect ratios (typically 720p or 1080p).
 */
export const STANDARD_VIDEO_ASPECT_RATIO_DIMENSIONS: Record<
  VideoAspectRatio,
  { width: number; height: number }
> = {
  '16:9': { width: 1280, height: 720 },
  '9:16': { width: 720, height: 1280 },
  '1:1': { width: 720, height: 720 },
  '4:3': { width: 960, height: 720 },
  '21:9': { width: 1680, height: 720 },
};

/**
 * Resolves standard pixel dimensions based on requested video aspect ratio.
 */
export function resolveVideoDimensions(
  aspectRatio?: VideoAspectRatio,
  customWidth?: number,
  customHeight?: number
): { width: number; height: number } {
  if (customWidth && customHeight) {
    return { width: customWidth, height: customHeight };
  }

  const ratio = aspectRatio || '16:9';
  return (
    STANDARD_VIDEO_ASPECT_RATIO_DIMENSIONS[ratio] || {
      width: 1280,
      height: 720,
    }
  );
}

/**
 * Registry and factory for VideoGeneratorDriver implementations.
 */
export class VideoDriverFactory {
  private static drivers: Map<string, VideoGeneratorDriver> = new Map();

  /**
   * Registers a video generator driver implementation.
   */
  public static registerDriver(driver: VideoGeneratorDriver): void {
    this.drivers.set(driver.providerCode.toLowerCase(), driver);
  }

  /**
   * Retrieves a driver by its provider code (e.g. 'fal', 'runway', 'synthesia').
   */
  public static getDriverByProvider(
    provider: VideoProviderCode
  ): VideoGeneratorDriver {
    const key = provider.toLowerCase();
    const driver = this.drivers.get(key);

    if (!driver) {
      throw new Error(
        `VideoGeneratorDriver not found for provider '${provider}'. Registered providers: [${Array.from(
          this.drivers.keys()
        ).join(', ')}]`
      );
    }

    return driver;
  }

  /**
   * Finds the appropriate video driver that supports the requested model.
   */
  public static getDriverForModel(model: string): VideoGeneratorDriver {
    const normalizedModel = model.toLowerCase().trim();

    for (const driver of this.drivers.values()) {
      if (driver.supportsModel(normalizedModel)) {
        return driver;
      }
    }

    throw new Error(
      `No registered VideoGeneratorDriver supports model '${model}'. Registered providers: [${Array.from(
        this.drivers.keys()
      ).join(', ')}]`
    );
  }

  /**
   * Checks whether any registered driver supports the specified video model.
   */
  public static hasDriverForModel(model: string): boolean {
    const normalizedModel = model.toLowerCase().trim();
    for (const driver of this.drivers.values()) {
      if (driver.supportsModel(normalizedModel)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Lists all registered driver provider codes.
   */
  public static getRegisteredProviders(): string[] {
    return Array.from(this.drivers.keys());
  }

  /**
   * Clears all registered drivers (primarily for testing purposes).
   */
  public static clear(): void {
    this.drivers.clear();
  }

  /**
   * Resets and re-registers standard platform default drivers.
   */
  public static registerDefaultDrivers(): void {
    this.clear();
    this.registerDriver(new FalVideoDriver());
    this.registerDriver(new RunwayVideoDriver());
    this.registerDriver(new SynthesiaVideoDriver());
  }

  /**
   * Resets and re-registers standard platform default drivers.
   */
  public static initDefaults(): void {
    this.registerDefaultDrivers();
  }
}

// Automatically register default drivers
VideoDriverFactory.initDefaults();
